import type { DealStatus } from '@ayocore/erp/crm/schemas/deal-status.schema';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import { useTab } from '@ayocore/platform/client/tabs';
import { useDealRepository, useDealEventRepository, useDealStatusRepository } from '../../../rpc';
import { DealViewController, type DealViewState } from './DealViewController';

function withActions(
  controller: DealViewController,
  snapshot: DealViewState,
  dealId: number,
  isModal: boolean,
  setTabTitle: (title: string) => void,
  navigate: ReturnType<typeof useNavigate>,
  dealRepository: ReturnType<typeof useDealRepository>,
  dealEventRepository: ReturnType<typeof useDealEventRepository>,
  dealStatusRepository: ReturnType<typeof useDealStatusRepository>
) {
  return {
    // State
    deal: snapshot.deal,
    statusHistory: snapshot.statusHistory,
    financialSummary: snapshot.financialSummary,
    loading: snapshot.loading,
    error: snapshot.error,
    permissions: snapshot.permissions,
    modals: snapshot.modals,
    statusChangeTarget: snapshot.statusChangeTarget,
    statusChangeError: snapshot.statusChangeError,
    statusChanging: snapshot.statusChanging,

    // Actions
    reload: () =>
      controller.reload(
        dealId,
        isModal,
        setTabTitle,
        navigate,
        dealRepository,
        dealEventRepository
      ),
    updateDealField: (
      field:
        | 'partner_id'
        | 'deal_owner_id'
        | 'description'
        | 'notes'
        | 'delivery_date_plan'
        | 'close_date_fact',
      value: number | string | null
    ) =>
      controller.updateDealField(
        dealId,
        field,
        value as import('../../../../schemas/deal.schema').Deal[typeof field],
        dealRepository
      ),
    openEdit: () => controller.openEdit(),
    closeEdit: () => controller.closeEdit(),
    openDelete: () => controller.openDelete(),
    closeDelete: () => controller.closeDelete(),
    deleteDeal: () => controller.deleteDeal(dealId, navigate, dealRepository),
    openStatusChange: (status: DealStatus) => controller.openStatusChange(status),
    closeStatusChange: () => controller.closeStatusChange(),
    changeStatus: (cancelComment?: string) =>
      controller.changeStatus(dealId, dealRepository, dealEventRepository, cancelComment),
    checkCanArchive: () => controller.checkCanArchive(dealId, dealRepository),
    archiveDeal: () =>
      controller.archiveDeal(
        dealId,
        isModal,
        setTabTitle,
        navigate,
        dealRepository,
        dealEventRepository
      ),
    unarchiveDeal: () =>
      controller.unarchiveDeal(
        dealId,
        isModal,
        setTabTitle,
        navigate,
        dealRepository,
        dealEventRepository
      ),
    cancelDeal: () =>
      controller.cancelDeal(
        dealId,
        isModal,
        setTabTitle,
        navigate,
        dealRepository,
        dealEventRepository,
        dealStatusRepository
      ),
    findCancelStatus: () => controller.findCancelStatus(dealStatusRepository),
  };
}

export function useDealViewController(
  dealId: number,
  isModal?: boolean
): ReturnType<typeof withActions>;
export function useDealViewController<S>(
  dealId: number,
  isModal: boolean | undefined,
  selector: (full: ReturnType<typeof withActions>) => S
): S;
export function useDealViewController<S = ReturnType<typeof withActions>>(
  dealId: number,
  isModal: boolean = false,
  selector?: (full: ReturnType<typeof withActions>) => S
): S | ReturnType<typeof withActions> {
  const controllerRef = useRef<DealViewController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new DealViewController();
  }
  const controller = controllerRef.current;
  const { setTabTitle } = useTab();
  const navigate = useNavigate();
  const dealRepository = useDealRepository();
  const dealEventRepository = useDealEventRepository();
  const dealStatusRepository = useDealStatusRepository();

  const setTabTitleRef = useRef(setTabTitle);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    setTabTitleRef.current = setTabTitle;
    navigateRef.current = navigate;
  }, [setTabTitle, navigate]);

  useEffect(() => {
    if (!dealId || Number.isNaN(dealId)) return;
    controller.load(
      dealId,
      isModal,
      setTabTitleRef.current,
      navigateRef.current,
      dealRepository,
      dealEventRepository
    );
    controller.checkPermissions(dealId, dealRepository);
  }, [dealId, isModal, controller]);

  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );

  const full = useMemo(
    () =>
      withActions(
        controller,
        state,
        dealId,
        isModal,
        setTabTitle,
        navigate,
        dealRepository,
        dealEventRepository,
        dealStatusRepository
      ),
    [
      controller,
      state,
      dealId,
      isModal,
      setTabTitle,
      navigate,
      dealRepository,
      dealEventRepository,
      dealStatusRepository,
    ]
  );

  if (!selector) return full;

  const selectedRef = useRef<S | undefined>(undefined);
  return useMemo(() => {
    const selected = selector(full);
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        /* noop */
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [full, selector]);
}
